import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { useConfig } from "../lib/format";
import { LANGUAGES } from "../i18n/translations";
import { PageHeader } from "../components/shared";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

export default function Account() {
  const { t, setLang } = useI18n();
  const { user, setUser } = useAuth();
  const config = useConfig();
  const [form, setForm] = useState({
    name: user?.name || "",
    language: user?.language || "pt",
    country: user?.country || "BR",
    currency: user?.currency || "BRL",
  });
  const [saving, setSaving] = useState(false);

  const onCountry = (code) => {
    const c = config?.countries.find((x) => x.code === code);
    setForm((f) => ({ ...f, country: code, currency: c?.currency || f.currency }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", form);
      setUser({ ...user, ...data });
      setLang(form.language);
      toast.success(t("account.saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold";

  return (
    <div>
      <PageHeader title={t("account.title")} testid="account-header" />
      <div className="glass max-w-xl space-y-5 rounded-2xl p-6 sm:p-8" data-testid="account-form">
        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("auth.name")}</label>
          <input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("auth.email")}</label>
          <input value={user?.email || ""} disabled className={`${inputCls} opacity-60`} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("account.language")}</label>
            <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
              <SelectTrigger data-testid="account-language-select" className="w-full rounded-xl border-xmas-gold/20 bg-xmas-navy/50 text-sm text-xmas-snow">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-xmas-gold/20 bg-xmas-navycard text-xmas-snow">
                {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("account.country")}</label>
            <Select value={form.country} onValueChange={onCountry}>
              <SelectTrigger data-testid="account-country-select" className="w-full rounded-xl border-xmas-gold/20 bg-xmas-navy/50 text-sm text-xmas-snow">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-xmas-gold/20 bg-xmas-navycard text-xmas-snow">
                {(config?.countries || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name?.[form.language] || c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("account.currency")}</label>
            <input value={form.currency} disabled className={`${inputCls} opacity-60`} />
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="account-save-button" className="btn-gold rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
          {saving ? "..." : t("cta.save")}
        </button>
      </div>
    </div>
  );
}
